// Ultimate Card Game (UCG) - AI Adaptive Session Memory
//
// Tracks player deck choices across matches in the current session.
// Governs the learning curve and "grace period" per difficulty tier:
//   - Easy: 0% adaptation (stays completely random/naive).
//   - Medium: Slow adaptation (requires 3-4 consistent matches, pure faction).
//   - Hard: Adapts after 2 matches; full cross-faction drafting.
//   - Very Hard: Adapts after 1 match; full cross-faction drafting.
//   - Invincible: Immediate 1-match adaptation; full cross-faction drafting.
//
// Once the player changes their strategy and springs a surprise trap, the grace
// period is consumed ("aufgebraucht"), causing the AI to adapt immediately
// on the very next match.

import { getCardByName } from '../../data/cards.js';
import { LARGE_DECK_MIN, classifyLargeDeck } from './deckProfile.js';

const STORAGE_KEY_ADAPTIVE_MEM = 'ucg_ai_adaptive_memory';

export class AdaptiveMemory {
  // `persist: false` gives a throwaway memory that never reads or writes
  // sessionStorage - for the headless eval harness, which must not touch the
  // memory of a real session (js/admin/headlessSim.js can run in the browser).
  // `shareClassifier: false` keeps the old absolute-count reading of large decks
  // (everything reads as "combined"); only the eval harness turns it off, to
  // measure what the repaired classifier is worth.
  constructor({ persist = true, shareClassifier = true } = {}) {
    this.persist = persist;
    this.shareClassifier = shareClassifier;
    this.sessionMatches = [];
    this.isGracePeriodBurned = false;
    this.lastRecordedThreat = null;
    this.lastBotArchetype = null;
    this.recentBotArchetypes = [];
    this.consecutiveLosses = 0;
    this.consecutiveWins = 0;
    this._loadFromStorage();
  }

  _loadFromStorage() {
    if (!this.persist) return;
    try {
      if (typeof sessionStorage !== 'undefined') {
        const raw = sessionStorage.getItem(STORAGE_KEY_ADAPTIVE_MEM);
        if (raw) {
          const data = JSON.parse(raw);
          if (Array.isArray(data.sessionMatches)) this.sessionMatches = data.sessionMatches;
          if (typeof data.consecutiveLosses === 'number') this.consecutiveLosses = data.consecutiveLosses;
          if (typeof data.consecutiveWins === 'number') this.consecutiveWins = data.consecutiveWins;
          if (typeof data.isGracePeriodBurned === 'boolean') this.isGracePeriodBurned = data.isGracePeriodBurned;
          if (data.lastRecordedThreat) this.lastRecordedThreat = data.lastRecordedThreat;
          if (data.lastBotArchetype) this.lastBotArchetype = data.lastBotArchetype;
          if (Array.isArray(data.recentBotArchetypes)) this.recentBotArchetypes = data.recentBotArchetypes;
        }
      }
    } catch {
      // sessionStorage unavailable or parse error - fail silent
    }
  }

  _saveToStorage() {
    if (!this.persist) return;
    try {
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem(STORAGE_KEY_ADAPTIVE_MEM, JSON.stringify({
          sessionMatches: this.sessionMatches.slice(-10),
          consecutiveLosses: this.consecutiveLosses,
          consecutiveWins: this.consecutiveWins,
          isGracePeriodBurned: this.isGracePeriodBurned,
          lastRecordedThreat: this.lastRecordedThreat,
          lastBotArchetype: this.lastBotArchetype,
          recentBotArchetypes: this.recentBotArchetypes
        }));
      }
    } catch {
      // fail silent
    }
  }

  reset() {
    this.sessionMatches = [];
    this.isGracePeriodBurned = false;
    this.lastRecordedThreat = null;
    this.lastBotArchetype = null;
    this.recentBotArchetypes = [];
    this.consecutiveLosses = 0;
    this.consecutiveWins = 0;
    if (!this.persist) return;
    try {
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.removeItem(STORAGE_KEY_ADAPTIVE_MEM);
      }
    } catch {
      // fail silent
    }
  }

  // Analyzes an array of card names or card objects and computes threat ratios
  analyzeDeckThreats(cardList) {
    if (!cardList || cardList.length === 0) {
      return {
        primaryThreat: 'balanced',
        secondaryThreat: null,
        isCombinedArms: false,
        hasStealth: false,
        hasArtillery: false,
        hasBoardWipe: false,
        hasHacker: false,
        avgCost: 10,
        airRatio: 0,
        swarmRatio: 0,
        heavyRatio: 0,
        stealthRatio: 0,
        totalCards: 0
      };
    }

    let airCount = 0;
    let swarmCount = 0;
    let heavyCount = 0;
    let stealthCount = 0;
    let totalCards = 0;
    let totalCost = 0;

    for (const item of cardList) {
      const card = typeof item === 'string' ? getCardByName(item) : (item?.name ? getCardByName(item.name) || item : null);
      if (!card) continue;
      totalCards++;
      totalCost += (card.cost || 10);

      const isAir = Boolean(card.isAir || card.isFlyer || card.elementType === 'Air');
      const isStealth = Boolean(card.isCloaked || card.name === 'Shade' || card.name === 'Anti Shade');
      const isSwarm = !isAir && (card.spellEffect === 'fodder' || card.name === 'Fodder' || (!isStealth && ((card.cost || 10) <= 7 || card.tier === 1)));
      const isHeavy = !isAir && !isSwarm && !isStealth && (card.type === 'Unit' || card.type === 'Structure' || card.type === 'Module') && ((card.cost || 0) >= 20 || (card.hp || 0) >= 200 || card.isStructure || card.name === 'Panzer' || card.name === 'Deployable Cannon' || card.name === 'Preserver Lock' || card.name === 'A9 Grizzly' || card.name === 'Orchestra');

      if (isAir) airCount++;
      if (isStealth) stealthCount++;
      if (isHeavy) heavyCount++;
      if (isSwarm) swarmCount++;
    }

    const n = Math.max(1, totalCards);
    const airRatio = airCount / n;
    const swarmRatio = swarmCount / n;
    const heavyRatio = heavyCount / n;
    const stealthRatio = stealthCount / n;
    const avgCost = totalCost / n;

    // Specific key threat indicators
    const hasStealth = stealthCount >= 1;
    const hasArtillery = cardList.some(item => {
      const name = typeof item === 'string' ? item : item?.name;
      return name === 'Orchestra' || name === 'Harvesting Pillar';
    });
    const hasBoardWipe = cardList.some(item => {
      const name = typeof item === 'string' ? item : item?.name;
      return name === 'Experimental Metamorphosis';
    });
    const hasHacker = cardList.some(item => {
      const name = typeof item === 'string' ? item : item?.name;
      return name === 'Hacker';
    });

    let structureCount = 0;
    for (const item of cardList) {
      const card = typeof item === 'string' ? getCardByName(item) : (item?.name ? getCardByName(item.name) || item : null);
      if (!card) continue;
      if (card.isStructure || card.type === 'Structure' || card.deployAtBonusIndicator || card.name === 'Deployable Cannon' || card.name === 'Monitor Lock' || card.name === 'Preserver Lock' || card.name === 'Sentinal Lock') {
        structureCount++;
      }
    }
    const hasSiegeFortress = Boolean(hasArtillery && structureCount >= 1);

    // Multi-Threat / Combined Arms condition:
    // A deck is Combined Arms when multiple distinct major archetypes are strongly represented
    const isCombinedArms = (
      (airCount >= 2 && heavyCount >= 2) ||
      (airCount >= 2 && swarmCount >= 3) ||
      (heavyCount >= 2 && swarmCount >= 3) ||
      (stealthCount >= 1 && airCount >= 2 && heavyCount >= 2)
    );

    let primaryThreat = 'balanced';
    let secondaryThreat = null;

    if (isCombinedArms) {
      if (airCount === heavyCount || (airCount >= 3 && heavyCount >= 3)) {
        primaryThreat = 'combined';
        secondaryThreat = heavyCount >= airCount ? 'heavy' : 'air';
      } else if (airCount > heavyCount && airCount > swarmCount) {
        primaryThreat = 'air';
        secondaryThreat = heavyCount >= swarmCount ? 'heavy' : 'swarm';
      } else if (heavyCount >= airCount && heavyCount > swarmCount) {
        primaryThreat = 'heavy';
        secondaryThreat = airCount >= swarmCount ? 'air' : 'swarm';
      } else {
        primaryThreat = 'combined';
        secondaryThreat = 'heavy';
      }
    } else if (hasSiegeFortress) {
      primaryThreat = 'siege_fortress';
      secondaryThreat = airCount >= 2 ? 'air' : (heavyCount >= 1 ? 'heavy' : 'swarm');
    } else if (stealthCount >= 2 || (stealthCount >= 1 && stealthRatio >= 0.2)) {
      primaryThreat = 'stealth';
    } else if (airCount >= 2 || (airCount >= 1 && airRatio >= 0.25)) {
      primaryThreat = 'air';
    } else if (heavyCount >= 2 && heavyCount > swarmCount) {
      primaryThreat = 'heavy';
    } else if (swarmCount >= 2 && swarmCount >= heavyCount) {
      primaryThreat = 'swarm';
    } else if (heavyRatio >= 0.3) {
      primaryThreat = 'heavy';
    } else if (swarmRatio >= 0.3) {
      primaryThreat = 'swarm';
    }

    // A 40-card player deck reaches every absolute count above, so it would
    // always read as "combined" (docs/AI_MASTERPLAN_2026-10.md, B20). Large
    // decks are judged by what stands out against a random deck of that size.
    let large = null;
    if (this.shareClassifier && totalCards >= LARGE_DECK_MIN) {
      large = classifyLargeDeck(cardList);
      primaryThreat = large.primary;
      secondaryThreat = large.secondary;
    }

    return {
      primaryThreat,
      secondaryThreat,
      isCombinedArms: large ? large.combined : isCombinedArms,
      deckProfile: large ? { shares: large.shares, z: large.z, traits: large.traits } : null,
      hasStealth,
      hasArtillery,
      hasBoardWipe,
      hasHacker: Boolean(hasHacker),
      hasSiegeFortress,
      structureCount,
      avgCost,
      airRatio,
      swarmRatio,
      heavyRatio,
      stealthRatio,
      airCount,
      swarmCount,
      heavyCount,
      stealthCount,
      totalCards
    };
  }

  // Records the AI's archetype choice to enforce diversity and rotation across matches
  recordAiArchetype(archetypeId) {
    if (!archetypeId) return;
    const baseId = String(archetypeId).replace(/_anti_.*$/, '');
    this.lastBotArchetype = baseId;
    if (!Array.isArray(this.recentBotArchetypes)) this.recentBotArchetypes = [];
    this.recentBotArchetypes = this.recentBotArchetypes.filter(id => id !== baseId);
    this.recentBotArchetypes.push(baseId);
    if (this.recentBotArchetypes.length > 3) {
      this.recentBotArchetypes.shift();
    }
    this._saveToStorage();
  }

  // Records the conclusion of a match
  recordMatch({ playerCards = [], botWon = false, difficulty = 'hard', botArchetype = null } = {}) {
    if (botArchetype) {
      this.recordAiArchetype(botArchetype);
    }
    if (!playerCards || playerCards.length === 0) return;
    if (difficulty === 'easy') return; // Easy matches do not train, burn grace, or alter adaptive session memory

    if (botWon) {
      this.consecutiveLosses = 0;
      this.consecutiveWins = (this.consecutiveWins || 0) + 1;
    } else {
      this.consecutiveLosses = (this.consecutiveLosses || 0) + 1;
      this.consecutiveWins = 0;
    }

    const names = playerCards.map(c => (typeof c === 'string' ? c : c?.name || '')).filter(Boolean);
    const analysis = this.analyzeDeckThreats(names);

    // If player switched to a completely different primary threat compared to last match,
    // the surprise trap triggers and immediately burns the grace period!
    if (this.lastRecordedThreat && this.lastRecordedThreat !== analysis.primaryThreat && this.lastRecordedThreat !== 'balanced') {
      this.isGracePeriodBurned = true;
    }
    this.lastRecordedThreat = analysis.primaryThreat;

    this.sessionMatches.push({
      cards: names,
      analysis,
      botWon,
      difficulty,
      timestamp: Date.now()
    });

    // Natural burn of grace period based on matches played
    // On Hard: initial adaptation begins after 1 match (50% optimization);
    // full/maximal adaptation (100%) kicks in after 2 matches.
    const fullThreshold = difficulty === 'hard' ? 2 : this.getInitialGraceThreshold(difficulty);
    if (this.sessionMatches.length >= fullThreshold) {
      this.isGracePeriodBurned = true;
    }
    this._saveToStorage();
  }

  getInitialGraceThreshold(difficulty) {
    switch (difficulty) {
      case 'invincible':
      case 'insane':
        return 0; // Immediate 1-match reaction
      case 'veryHard':
        return 1; // Adapts after 1 match (100% maximal)
      case 'hard':
        return 1; // Adapts after 1 match (50% on match 1, 100% maximal on match 2+)
      case 'medium':
        return 3; // Adapts after 3 matches
      case 'easy':
      default:
        return Infinity; // Never adapts
    }
  }

  // Returns player threat profile for deck builder, or null if still in grace period / Easy
  getPlayerThreatProfile(difficulty = 'hard') {
    if (difficulty === 'easy' || this.sessionMatches.length === 0) {
      return null;
    }

    const initialThreshold = this.getInitialGraceThreshold(difficulty);
    if (!this.isGracePeriodBurned && this.sessionMatches.length < initialThreshold) {
      return null;
    }

    // Blend recent matches (heavily weighting the latest match)
    const recent = this.sessionMatches[this.sessionMatches.length - 1];
    const isCrossFactionAllowed = ['hard', 'veryHard', 'insane', 'invincible'].includes(difficulty);

    const matchesCount = this.sessionMatches.length;
    let optimizationFactor = 1.0;
    if (difficulty === 'hard') {
      // 50% optimization after 1st match; 100% maximal optimization after 2+ matches
      optimizationFactor = (matchesCount <= 1 && !this.isGracePeriodBurned) ? 0.5 : 1.0;
    } else if (difficulty === 'medium') {
      optimizationFactor = 0.5;
    } else {
      optimizationFactor = 1.0;
    }

    let primaryThreat = recent.analysis.primaryThreat;
    let rotatedFromLoss = false;

    // Loss-Driven Threat Rotation & Champion Deck Mimicry:
    // Only rotates if the opponent deck actually has combined arms / secondary threat vectors!
    let isChampionMimicry = false;
    let championDeck = null;

    if (this.consecutiveLosses >= 4) {
      // 4 consecutive losses: Player has proven an unbreakable championship-tier meta deck!
      // AI mirrors the winning champion deck archetype and techs with counter cards!
      isChampionMimicry = true;
      championDeck = (recent.cards || []).map(c => typeof c === 'string' ? c : c?.name).filter(Boolean);
    } else if (this.consecutiveLosses >= 2 && recent.analysis.isCombinedArms) {
      if (recent.analysis.secondaryThreat && primaryThreat === 'combined') {
        // If the combined general counter didn't win, pivot to the secondary concrete vector (e.g. heavy/artillery)
        primaryThreat = recent.analysis.secondaryThreat;
        rotatedFromLoss = true;
      }
    }

    return {
      primaryThreat,
      secondaryThreat: recent.analysis.secondaryThreat,
      isCombinedArms: recent.analysis.isCombinedArms,
      deckProfile: recent.analysis.deckProfile || null,
      hasStealth: recent.analysis.hasStealth,
      hasArtillery: recent.analysis.hasArtillery,
      hasBoardWipe: recent.analysis.hasBoardWipe,
      hasSiegeFortress: recent.analysis.hasSiegeFortress,
      structureCount: recent.analysis.structureCount,
      avgCost: recent.analysis.avgCost,
      consecutiveLosses: this.consecutiveLosses,
      consecutiveWins: this.consecutiveWins || 0,
      hasWinStreakRelief: (this.consecutiveWins || 0) >= 3,
      rotatedFromLoss,
      isChampionMimicry,
      championDeck,
      airRatio: recent.analysis.airRatio,
      swarmRatio: recent.analysis.swarmRatio,
      heavyRatio: recent.analysis.heavyRatio,
      stealthRatio: recent.analysis.stealthRatio,
      isCrossFactionAllowed,
      optimizationFactor,
      recentCards: recent.cards,
      sessionMatchesCount: matchesCount,
      isGracePeriodBurned: this.isGracePeriodBurned,
      lastBotArchetype: this.lastBotArchetype || null,
      recentBotArchetypes: Array.isArray(this.recentBotArchetypes) ? [...this.recentBotArchetypes] : []
    };
  }
}

// Global session singleton instance
export const aiAdaptiveMemory = new AdaptiveMemory();
